/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import {
    GSLBService,
    ISplitDomainName,
} from 'ajs/modules/gslb/factories/gslb-service/gslb-service.item.factory';

import {
    GslbService,
    GslbServiceDownResponse,
} from 'object-types';

import { ClrFormLayout } from '@clr/angular';

import {
    PersistenceProfileCollection,
    TPersistenceProfileCollection,
} from 'ajs/modules/persistence-profile/factories/persistence-profile.collection.factory';

import {
    PKIProfileCollection,
    TPkiProfileCollection,
} from 'ajs/modules/security/factories/pki-profile/pki-profile.collection.factory';

import {
    GslbServiceDownResponseType,
    GslbServiceRecordType,
    PersistenceProfileType,
} from 'generated-types';

import { IEnumValue } from 'ajs/modules/core/services/schema-service/schema.types';
import './gslb-service-modal.component.less';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import * as l10n from './gslb-service-modal.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description Component for GSLB Service Item Modal.
 * @author Suraj Kumar, Umesh Chovatiya, Deepak Chaudhary
 */
@Component({
    selector: 'gslb-service-modal',
    templateUrl: './gslb-service-modal.component.html',
})
export class GslbServiceModalComponent implements OnDestroy, OnInit {
    @Input()
    public editable: GSLBService;

    /**
     * Flag to check if the GSLB Service Item is being edited, not created.
     */
    public isEditing = false;

    /**
     * Check whether the GSLB service type is SRV or not.
     */
    public isGSLBServiceRecordTypeSRV = false;

    public readonly persistenceProfileCollection: PersistenceProfileCollection;

    public readonly pkiProfileCollection: PKIProfileCollection;

    /**
     * Rows for Domain Names grid.
     */
    public splitDomainNames: ISplitDomainName[] = [];

    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Object types for Gslb Service Modal.
     */
    public readonly objectType = {
        GslbService,
        GslbServiceDownResponse,
    };

    constructor(
        l10nService: L10nService,
        @Inject(PersistenceProfileCollection)
        PersistenceProfileCollection: TPersistenceProfileCollection,
        @Inject(PKIProfileCollection)
        PKIProfileCollection: TPkiProfileCollection,
    ) {
        const params = { is_federated: true };
        const persistenceProfileParams = {
            ...params,
            persistence_type: PersistenceProfileType.PERSISTENCE_TYPE_GSLB_SITE,
        };

        this.persistenceProfileCollection = new PersistenceProfileCollection({
            params: persistenceProfileParams,
            defaults: persistenceProfileParams,
        });

        this.pkiProfileCollection = new PKIProfileCollection({
            params,
            defaults: params,
        });

        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Return true if fallback settings (IP, CNAME) should be configurable.
     */
    public get showFallbackSettings(): boolean {
        return this.isFallbackCnameDownResponse || this.isFallbackIpDownResponse;
    }

    /**
     * Return true if the configured fallback is IP.
     */
    public get isFallbackIpDownResponse(): boolean {
        return this.editable.isDownResponseType(
            GslbServiceDownResponseType.GSLB_SERVICE_DOWN_RESPONSE_FALLBACK_IP,
        );
    }

    /**
     * Return true if the configured fallback is CNAME.
     */
    public get isFallbackCnameDownResponse(): boolean {
        return this.editable.isDownResponseType(
            GslbServiceDownResponseType.GSLB_SERVICE_DOWN_RESPONSE_CNAME,
        );
    }

    /**
     * Return the configured fallback IPv4.
     */
    public get fallbackIpAddress(): string | undefined {
        return this.editable.config.down_response.config.fallback_ip.address;
    }

    /**
     * Return the configured fallback IPv6.
     */
    public get fallbackIp6Address(): string | undefined {
        return this.editable.config.down_response.config.fallback_ip6.address;
    }

    public handleGslbServiceChange(): void {
        const isGslbChecked = this.editable.config.enabled;

        const params = {
            is_federated: true,
            is_gslb_service: !isGslbChecked,
        };

        this.pkiProfileCollection.setNewItemDefaults(params);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.persistenceProfileCollection.destroy();
        this.pkiProfileCollection.destroy();
    }

    /** @override */
    public ngOnInit(): void {
        this.isEditing = Boolean(this.editable.id);

        this.handleRecordTypeChange(this.editable.config.record_type);
    }

    /**
     * Callback to ngFor-track by.
     */
    public trackByValue(index: number, type: IEnumValue): IEnumValue['value'] {
        return type.value;
    }

    /**
     * Handle Site Persistence selection change.
     */
    public handleSitePersistenceChange(): void {
        if (!this.editable.config.site_persistence_enabled) {
            this.editable.deleteSitePersistenceConfig();
        }
    }

    /**
     * Handle GSLB Service submit.
     */
    public submit(): void {
        this.editable.updateDomainNames(this.splitDomainNames);
        this.editable.submit();
    }

    /**
     * Handle the change in the GSLB Service Record Type.
     */
    public handleRecordTypeChange(gslbServiceRecordType: GslbServiceRecordType): void {
        this.isGSLBServiceRecordTypeSRV =
            gslbServiceRecordType === GslbServiceRecordType.GSLB_SERVICE_RECORD_TYPE_SRV;
    }
}
